// Shared presentational helpers for the Customer screens.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiChevronDown, FiChevronUp, FiSearch } from "react-icons/fi";

const PANEL_MAX_HEIGHT = 220;

/**
 * Mockup-styled single-select dropdown used across the Customer modals.
 * The options panel is rendered through a portal with `position: fixed`, so it
 * can never be clipped by a scrolling/`overflow-hidden` ancestor (cards/modals).
 * It tracks the trigger on scroll/resize and flips above the trigger when there
 * isn't enough room below.
 */
export function SelectDropdown({
  label,
  required,
  value,
  options = [],
  getLabel,
  getKey,
  onSelect,
  placeholder,
  error,
  searchable = false,
  searchPlaceholder = "Search…",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [panelStyle, setPanelStyle] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const visibleOptions = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const needle = query.trim().toLowerCase();
    return options.filter((option) => getLabel(option)?.toLowerCase().includes(needle));
  }, [searchable, query, options, getLabel]);

  // Reset the search box whenever the panel closes.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    // Flip above when there isn't room below but there is above.
    const openUp = spaceBelow < PANEL_MAX_HEIGHT && spaceAbove > spaceBelow;
    const maxHeight = Math.min(PANEL_MAX_HEIGHT, Math.max(openUp ? spaceAbove : spaceBelow, 120) - 8);

    setPanelStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      maxHeight,
      ...(openUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();

    const handle = () => updatePosition();
    window.addEventListener("scroll", handle, true); // capture: catch scrolls on any ancestor
    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
    };
  }, [open, updatePosition]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const handleClick = (event) => {
      if (triggerRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="block text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansSemiBold text-titleColor capitalize">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full h-[40px] px-3 rounded-[6px] border flex items-center justify-between text-[14px] leading-[20px] tracking-[0.14px] font-OpenSansRegular ${
          error ? "border-red-500" : "border-borderColor"
        } ${value ? "text-inputEmailTitle" : "text-subTitleColor"}`}
      >
        <span className="truncate">{value ? getLabel(value) : placeholder}</span>
        {open ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
      </button>

      {open &&
        panelStyle &&
        createPortal(
          <div
            ref={panelRef}
            style={panelStyle}
            data-tour="select-panel"
            className="flex flex-col bg-white border border-borderColor rounded-[8px] shadow-lg z-[1000]"
          >
            {searchable && (
              <div className="flex items-center gap-2 px-3 py-2 border-b border-borderColor">
                <FiSearch size={15} className="text-subTitleColor shrink-0" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full text-[13px] font-OpenSansRegular text-titleColor outline-none placeholder:text-subTitleColor"
                />
              </div>
            )}
            <div className="overflow-y-auto min-h-0">
              {visibleOptions.length === 0 ? (
                <div className="px-4 py-3 text-[13px] text-subTitleColor font-OpenSansRegular">
                  {options.length === 0 ? "No options available." : "No matches found."}
                </div>
              ) : (
                visibleOptions.map((option) => (
                  <div
                    key={getKey(option)}
                    onClick={() => {
                      onSelect(option);
                      setOpen(false);
                    }}
                    className="px-4 py-2.5 text-[13px] font-OpenSansRegular text-titleColor cursor-pointer hover:bg-backgroundColor"
                  >
                    {getLabel(option)}
                  </div>
                ))
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export function StatusBadge({ archived }) {
  if (archived) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-OpenSansSemiBold bg-[#F2F4F7] text-subTitleColor">
        Archived
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-OpenSansSemiBold bg-closedColor text-[#1F7A34]">
      Active
    </span>
  );
}

/**
 * Renders up to `max` branch chips, then a "+N" overflow chip.
 */
export function BranchChips({ branches = [], max = 2 }) {
  const unique = [...new Set(branches.filter(Boolean))];
  if (unique.length === 0) {
    return <span className="text-[13px] text-subTitleColor font-OpenSansRegular">—</span>;
  }

  const visible = unique.slice(0, max);
  const overflow = unique.length - visible.length;

  return (
    <div className="flex items-start gap-1 flex-wrap">
      {visible.map((name) => (
        <span
          key={name}
          className="inline-flex items-center px-2 py-0.5 rounded-[6px] text-[12px] font-OpenSansSemiBold leading-[18px] tracking-[0.12px] capitalize bg-[#EEF0F9] text-[#5F6477]"
        >
          {name}
        </span>
      ))}
      {overflow > 0 && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-[6px] text-[12px] font-OpenSansSemiBold leading-[18px] tracking-[0.12px] capitalize bg-[#EEF0F9] text-[#5F6477]">
          +{overflow}
        </span>
      )}
    </div>
  );
}
